import { Injectable, Injector } from '@angular/core';
import { FiBackendService } from '@fi-shared/fi-backend/fi-backend.service';
import { AgreementUserLogModel } from './agreement-user-log.model';
import { AgreementModel, IAgreementModel } from '@fi-shared/fi-agreement/agreement.model';
import { AppTextService } from '@fi-shared/app-text';
import { ContentLanguagesService } from '@fi-app/utilities/fi-services/content-languages.service';
import { AgreementPublishedContentModel } from '@fi-shared/fi-agreement/agreement-published-content.model';
import { FiContentLanguageSharedService } from '@fi-shared/fi-content-language-shared/fi-content-language-shared.service';
import { FiFormatDateService } from '@fi-shared/fi-format-date/fi-format-date.service';
import { IAgreementUserLog } from '@fi-app/agreement/agreement';

@Injectable()
export class AgreementService {
    TypeConfidential: number;

    constructor(
        private fiBackend: FiBackendService,
        private appTextService: AppTextService,
        private contentLanguagesService: ContentLanguagesService,
        private fiContentLanguagesSharedService: FiContentLanguageSharedService,
        private fiFormatDateService: FiFormatDateService,
        private injector: Injector,
    ) {
        this.TypeConfidential = AgreementModel.TypeConfidential;
    }

    getAgreementModelInstance(data: IAgreementModel) {
        return new AgreementModel(this.appTextService, this.fiFormatDateService, data);
    }

    getAgreementPublishedContentModelInstance(data) {
        return new AgreementPublishedContentModel(data);
    }

    GetLicenseAgreement() {
        const promise = new Promise<AgreementModel>((resolve, reject) => {
            this.fiBackend.fetchData<IAgreementModel>('Agreement/Agreement/GetLicenseAgreement').then((data) => {
                resolve(this.getAgreementModelInstance(data));
            }, reject);
        });
        return promise;
    }

    GetConfidentialAgreement() {
        const promise = new Promise<AgreementModel>((resolve, reject) => {
            return this.fiBackend
                .fetchData<IAgreementModel>('Agreement/Agreement/GetConfidentialAgreement')
                .then((data) => {
                    resolve(this.getAgreementModelInstance(data));
                }, reject);
        });
        return promise;
    }

    AcceptAgreement(agreement, contentLanguageId, acceptedByUserId, acceptedAgreementVersion) {
        const agreementUserLog = new AgreementUserLogModel(undefined, this.injector);
        agreementUserLog.AgreementId = agreement.Id;
        agreementUserLog.AgreementVersion = acceptedAgreementVersion;
        agreementUserLog.ContentLanguageId = contentLanguageId;
        agreementUserLog.AcceptedByUserId = acceptedByUserId;
        const promise = new Promise<AgreementUserLogModel>((resolve, reject) => {
            this.fiBackend.fetchDataPost('Agreement/Agreement/SaveAgreementUserLog', agreementUserLog).then((data) => {
                resolve(agreementUserLog);
            }, reject);
        });
        return promise;
    }

    GetAgreements(parameterApiModel): Promise<any[]> {
        const promise = new Promise<AgreementModel[]>((resolve, reject) => {
            this.fiBackend.fetchDataPost<any[]>('Agreement/Agreement/GetAgreements', parameterApiModel).then((data) => {
                const result: AgreementModel[] = [];
                data.forEach((item) => {
                    result.push(this.getAgreementModelInstance(item));
                });
                resolve(result);
            }, reject);
        });
        return promise;
    }

    GetAgreementsCount() {
        return this.fiBackend.fetchData('Agreement/Agreement/GetAgreementsCount');
    }

    GetAgreement(Id, initContentLanguages = false) {
        const promise = new Promise<AgreementModel>((resolve, reject) => {
            this.fiBackend.fetchData<IAgreementModel>(`Agreement/Agreement/GetAgreement/${Id}`).then((data) => {
                const result = this.getAgreementModelInstance(data);
                if (initContentLanguages) {
                    result.InitializeContentLanguages(this.fiContentLanguagesSharedService);
                }
                resolve(result);
            }, reject);
        });
        return promise;
    }

    RepublishAgreement(agreement) {
        return this.fiBackend.fetchDataPost(`Agreement/Agreement/RepublishAgreement/${agreement.Id}`);
    }

    GetAgreementUserLogs(agreement) {
        const promise = new Promise<AgreementUserLogModel[]>((resolve, reject) => {
            this.fiBackend
                .fetchData<IAgreementUserLog[]>(`Agreement/Agreement/GetAgreementUserLogs/${agreement.Id}`)
                .then((data) => {
                    const result: AgreementUserLogModel[] = [];
                    data.forEach((item) => {
                        result.push(new AgreementUserLogModel(item, this.injector));
                    });
                    resolve(result);
                }, reject);
        });
        return promise;
    }

    GetAgreementUserLog(agreementId, agreementUserLogId) {
        return this.fiBackend.fetchObject(
            `Agreement/Agreement/GetAgreementUserLog/${agreementId}/${agreementUserLogId}`,
            AgreementUserLogModel,
        );
    }

    SaveAgreement(agreement) {
        return this.fiBackend.fetchDataPost('Agreement/Agreement/SaveAgreement', agreement);
    }

    GenerateNewAgreementVersion(agreementId, selectedVersion, contentLanguageId) {
        return this.fiBackend.fetchData<void>('Agreement/Agreement/GenerateNewAgreementVersion', {
            reqConfig: {
                params: {
                    agreementId: agreementId,
                    selectedVersion: selectedVersion,
                    contentLanguageId: contentLanguageId,
                },
            },
        });
    }

    getAgreementName(agreement: AgreementModel) {
        return this.appTextService.get(
            agreement.AgreementType == AgreementModel.TypeLicence
                ? 'agreement.licence_agreement'
                : 'agreement.confidencial_agreement',
        );
    }
}
